import React from 'react'
import { T } from '@transifex/react'

import { pathJoin } from 'src/reducers/routing/utils'

// PRO-400: the CRM "Pricing" tab moved here as "Legacy Pricing"; its route
// subtree (containers / services / recurring / agreement-templates — the
// vat-rates key retired to Company Settings > Finance) is reused verbatim
// from the CRM section's pricing map.
import { createPricingRoutesMap } from './crm/pricing'

// LEDGER-809: detail/new titles must live on nodes NESTED under their tab's
// map entry (the containers pattern) — `getRouteTitle` walks the URL one
// segment at a time, so a slash-keyed sibling like 'products/:id' is
// unreachable once the literal `products` key has matched, and the header
// breadcrumb fell back to the module's "Product Management" title.
export const createProductManagementRoutesMap = (prefix: string) => ({
  // PRO-400: the moved CRM Pricing tab ("Legacy Pricing"). Same sub-routes as
  // the old crm/pricing so the tab's internal navigation is unchanged.
  'legacy-pricing': pathJoin(prefix)('legacy-pricing').chainMapAuto(
    createPricingRoutesMap
  ),
  // PRO-777: the agreement-template register moved OUT of Legacy pricing into
  // its own Products & prices surface. It is NOT legacy: its template products
  // were migrated onto the P&P catalogue (LEDGER-760/761 —
  // `crm.AgreementTemplateProduct.pp_product`) and the property creation/detail
  // views apply them, so it must not sit behind the retiring Legacy card. The
  // old `legacy-pricing/agreement-templates` URL redirects here — see
  // `createLegacyAgreementTemplatesRedirects` in
  // product-management/route-config.tsx.
  'agreement-templates': pathJoin(prefix)('agreement-templates').chainMap({
    ':id': (id = ':id') =>
      pathJoin(prefix)('agreement-templates')(id).chainWithTitle({
        title: <T _str="Agreement template details" />,
        breadCrumbTitle: <T _str="Agreement template details" />,
      }),
  }),
  categories: pathJoin(prefix)('categories').chainMap({
    // Tab is named "Product templates" (Categories → Product templates
    // naming pass), so the "<tab name> Details" convention follows it.
    ':id': (id = ':id') =>
      pathJoin(prefix)('categories')(id).chainWithTitle({
        title: <T _str="Product template details" />,
        breadCrumbTitle: <T _str="Product template details" />,
      }),
  }),
  // PRO-320: 'pricing-units' was removed — the Units catalog lives on
  // Company Settings > Units & formats now. The old path still redirects
  // (see product-management/route-config.tsx in the settings layer).
  'price-lists': pathJoin(prefix)('price-lists').chainMap({
    ':id': (id = ':id') =>
      pathJoin(prefix)('price-lists')(id).chainWithTitle({
        title: <T _str="Price List Details" />,
        breadCrumbTitle: <T _str="Price list details" />,
      }),
  }),
  // The Prices tab: one register of price rows with a direction switch.
  // `contractor` is a VIEW of the same tab, not a sibling tab, so it lives
  // under this key — that is what keeps "Prices" highlighted on both sides
  // (`TabsNavigationExtended` resolves the active tab from the first path
  // segment after the module root).
  prices: pathJoin(prefix)('prices').chainMap({
    contractor: pathJoin(prefix)('prices')('contractor').chainWithTitle({
      title: <T _str="Contractor prices" />,
      breadCrumbTitle: <T _str="Contractor prices" />,
    }),
  }),
  products: pathJoin(prefix)('products').chainMap({
    new: pathJoin(prefix)('products')('new').chainWithTitle({
      title: <T _str="New Product" />,
      breadCrumbTitle: <T _str="New product" />,
    }),
    ':id': (id = ':id') =>
      pathJoin(prefix)('products')(id).chainWithTitle({
        title: <T _str="Product Details" />,
        breadCrumbTitle: <T _str="Product details" />,
      }),
  }),
  'service-levels': pathJoin(prefix)('service-levels').chain,
  // DEV TASK 6: the contractor price side lives here (Contract management),
  // next to all other pricing. PRO-324: create/edit is the routed wizard —
  // `new` before `:id` so "/new" matches as a literal segment.
  'contractor-prices': pathJoin(prefix)('contractor-prices').chainMap({
    new: pathJoin(prefix)('contractor-prices')('new').chainWithTitle({
      title: <T _str="New contractor price" />,
      breadCrumbTitle: <T _str="New contractor price" />,
    }),
    ':id': (id = ':id') =>
      pathJoin(prefix)('contractor-prices')(id).chainWithTitle({
        title: <T _str="Edit contractor price" />,
        breadCrumbTitle: <T _str="Edit contractor price" />,
      }),
  }),
  'price-adjustments': pathJoin(prefix)('price-adjustments').chain,
  'index-adjustments': pathJoin(prefix)('index-adjustments').chain,
  'compensation-models': pathJoin(prefix)('compensation-models').chainMap({
    new: pathJoin(prefix)('compensation-models')('new').chainWithTitle({
      title: <T _str="New bonus & penalty model" />,
      breadCrumbTitle: <T _str="New bonus & penalty model" />,
    }),
    ':id': (id = ':id') =>
      pathJoin(prefix)('compensation-models')(id).chainWithTitle({
        title: <T _str="Edit bonus & penalty model" />,
        breadCrumbTitle: <T _str="Edit bonus & penalty model" />,
      }),
  }),
  // PRO-467: the set/schedule form is a routed detail keyed by the exception
  // type code (`:id`). No `new` route — effects are always set per known type.
  'exception-pay-effects': pathJoin(prefix)('exception-pay-effects').chainMap({
    ':id': (id = ':id') =>
      pathJoin(prefix)('exception-pay-effects')(id).chainWithTitle({
        title: <T _str="Set exception pay effect" />,
        breadCrumbTitle: <T _str="Set exception pay effect" />,
      }),
  }),
  // HSY 263: period-scoped reliability exclusion windows — a single list page
  // with create/edit in a modal (no routed detail).
  'reliability-exclusion-windows': pathJoin(prefix)(
    'reliability-exclusion-windows'
  ).chain,
  'bill-of-materials': pathJoin(prefix)('bill-of-materials').chain,
  'audit-logs': pathJoin(prefix)('audit-logs').chain,
  'price-determination-playground': pathJoin(prefix)(
    'price-determination-playground'
  ).chain,
  'timing-pricing': pathJoin(prefix)('timing-pricing').chain,
  setup: pathJoin(prefix)('setup').chain,
})
